import { Plus } from 'lucide-react';
import { Button, formatTimeAgo, SearchBox, type Column } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import tools from '../ListTools.module.css';
import { CarrierStatus } from '../orders/CarrierStatus.tsx';
import { OrderFilterBar } from '../orders/OrderFilterBar.tsx';
import {
  isFiltered,
  narrowingQueryOf,
  NO_ORDER_FILTERS,
  type OrderFilters,
} from '../orders/order-filters.ts';
import type { Order } from '../orders/orders.ts';
import { ORDER_PAGE_SIZE, useOrderList } from '../orders/useOrderList.ts';
import { ResourceTable } from '../ResourceTable.tsx';
import { useDebouncedValue } from '../useDebouncedValue.ts';
import { usePageContext } from '../usePageContext.ts';
import styles from './Returns.module.css';

const SEARCH_DELAY_MS = 300;

export type ReturnsToReceiveProps = {
  api: TypedClient;
  shipping: ShippingClient;
  filters: OrderFilters;
  onFiltersChange: (filters: OrderFilters) => void;
  inBasket: ReadonlySet<string>;
  carrierName: (code: string | null) => string;
  onAdd: (orders: readonly Order[]) => void;
};

export const ReturnsToReceive = (props: ReturnsToReceiveProps) => {
  const { api, shipping, filters, onFiltersChange, inBasket, carrierName, onAdd } = props;
  const { t, locale } = usePageContext();
  const [search, setSearch] = useState('');
  const [openedAt] = useState(() => new Date());
  const text = useDebouncedValue(search.trim(), SEARCH_DELAY_MS);
  const list = useOrderList(api, 'to_receive', narrowingQueryOf(filters, openedAt), { status: null, text });
  const shown = list.items.filter((order) => !inBasket.has(order.id));
  const narrowed = text !== '' || isFiltered(filters);
  const now = new Date();

  const columns: readonly Column<Order>[] = [
    {
      id: 'order',
      header: t('orders.column_order'),
      primary: true,
      cell: (order) => (
        <span className={styles['stacked']}>
          <strong>{order.number}</strong>
          <span className={styles['muted']}>{order.customer.name}</span>
        </span>
      ),
    },
    {
      id: 'carrier',
      header: t('orders.column_carrier'),
      cell: (order) => carrierName(order.dispatch.carrierCode),
    },
    {
      id: 'carrierStatus',
      status: true,
      header: t('orders.carrierStatus'),
      cell: (order) => <CarrierStatus dispatch={order.dispatch} />,
    },
    {
      id: 'since',
      header: t('returns.reported'),
      cell: (order) => formatTimeAgo(order.statusChangedAt, locale, now),
    },
    {
      id: 'add',
      header: t('returns.addShort'),
      action: true,
      cell: (order) => (
        <Button
          variant="outline"
          size="sm"
          icon={<Plus size={16} aria-hidden="true" />}
          aria-label={t('returns.add', { number: order.number })}
          onClick={() => {
            onAdd([order]);
          }}
        >
          {t('returns.addShort')}
        </Button>
      ),
    },
  ];

  return (
    <section aria-labelledby="returns-to-receive-title" className={styles['card']}>
      <div className={styles['cardHead']}>
        <h2 id="returns-to-receive-title">{t('returns.toReceiveTitle', { count: list.total })}</h2>
        {shown.length === 0 ? null : (
          <Button
            variant="outline"
            size="sm"
            icon={<Plus size={16} aria-hidden="true" />}
            onClick={() => {
              onAdd(shown);
            }}
          >
            {t('returns.addAll', { count: shown.length })}
          </Button>
        )}
      </div>
      <p className={styles['muted']}>{t('returns.toReceiveHelp')}</p>
      <ResourceTable
        bare
        query={list.query}
        columns={columns}
        rows={shown}
        rowKey={(order) => order.id}
        toolbar={
          <div className={tools['listTools']}>
            <SearchBox
              label={t('returns.searchLabel')}
              placeholder={t('returns.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              onSearch={setSearch}
            />
            <OrderFilterBar api={api} shipping={shipping} filters={filters} onChange={onFiltersChange} />
            {narrowed ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  onFiltersChange(NO_ORDER_FILTERS);
                }}
              >
                {t('common.clearFilters')}
              </Button>
            ) : null}
          </div>
        }
        pagination={{
          page: list.page,
          pageSize: ORDER_PAGE_SIZE,
          total: list.total,
          onPageChange: list.turnTo,
        }}
        emptyTitle={narrowed ? t('returns.toReceiveNoMatch') : t('returns.toReceiveEmpty')}
      />
    </section>
  );
};
